import React, { useState, useEffect } from "react";
import DataTable from "../components/DataTable.js";
import { fetchDataFromAPI, saveData, deleteData } from "../components/api.js";
import subscriptionStatus from "../components/SubscriptionStatus.js";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import config from "../config";

const baseUrl = config.baseURL;
const getRangeInputProps = (col) => {
  if (col.type === "range") {
    return { min: 0, max: 5, step: 0.5 };
  }
  return {};
};

const Marketing = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState("");
  const [totalItems, setTotalItems] = useState("");

  const [filters, setFilters] = useState({});
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [dataSource, setDataSource] = useState("customers"); // ✅ toggle between "customers" and "potential-customers"
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: "",
  });
  const isSubscribed = subscriptionStatus();
  const T = {
    dark: "#2E4053",
    darkDeep: "#1C2A3D",
    muted: "#34495E",
    border: "#A6AAAD",
    surface: "#F4F6F9",
    surfaceAlt: "#D1D8E0",
    chipText: "#D1D8E0",
    white: "#FFFFFF",
  };

  const primaryButtonStyles = {
    backgroundColor: T.dark,
    color: T.white,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    padding: "10px",
    fontSize: 15,

    boxShadow: "0 4px 16px rgba(46,64,83,.2)",
    transition: "transform .15s, background-color .15s",
    "&:hover": {
      backgroundColor: T.darkDeep,
      transform: "scale(1.01)",
    },
  };
  const secondaryButtonStyles = {
    backgroundColor: "transparent",
    color: T.dark,
    border: `1px solid ${T.border}`,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    padding: "10px",
    fontSize: 15,
    transition: "transform .15s, background-color .15s, border-color .15s",
    "&:hover": {
      backgroundColor: T.surfaceAlt,
      borderColor: T.dark,
      transform: "scale(1.01)",
    },
  };

  const cancelButtonStyles = {
    color: T.muted,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 15,
    "&:hover": {
      backgroundColor: T.surface,
    },
  };
  useEffect(() => {
    fetchData();
  }, [filters, sortField, sortOrder, dataSource]);

  const fetchData = async (page = 1, itemsPerPage = 10, filters) => {
    try {
      const token = localStorage.getItem("token");

      const filterParamsObj = {
        page,
        limit: itemsPerPage,
        ...filters,
        includeDeleted: "false",
      };

      if (sortField) filterParamsObj.sortField = sortField;
      if (sortOrder) filterParamsObj.sortOrder = sortOrder;

      const filterParams = new URLSearchParams(filterParamsObj).toString();

      // Dynamic endpoint switch
      const endpoint =
        dataSource === "customers" ? "/api/customers" : "/api/marketing";

      const { data: results, pagination } = await fetchDataFromAPI(
        endpoint,
        filterParams,
        token
      );
      console.log("data", results);
      console.log("pagination", pagination);
      setData(results || []);
      setTotalPages(pagination?.totalPages || 1);
      setTotalItems(pagination?.totalItems || 1);
    } catch (err) {
      console.error("Error fetching data:", err.message);
      setError(err.message);
    }
  };

  const handleAddRow = async () => {
    try {
      const newRow = {
        name: "New record",
      };

      const endpoint =
        dataSource === "customers" ? "/api/customers" : "/api/marketing";

      if (isOffline) {
        storeOfflineData([newRow], dataSource, "add");
      } else {
        await saveData(endpoint, newRow, "POST");
      }
      fetchData();
    } catch (err) {
      console.error("Error adding row:", err);
    }
  };
  const updateEmailField = (field, value) =>
    setEmailForm((prev) => ({ ...prev, [field]: value }));

  const openInEmailApp = () => {
    const mailto =
      `mailto:${emailForm.to}` +
      `?subject=${encodeURIComponent(emailForm.subject)}` +
      `&body=${encodeURIComponent(emailForm.body)}`;
    window.location.href = mailto;
  };
  const sendEmail = async () => {
    try {
      const token = localStorage.getItem("token");

      const emailData = {
        to: emailForm.to,
        subject: emailForm.subject,
        htmlContent: emailForm.body.replace(/\n/g, "<br />"),
        textContent: emailForm.body,
        companyId: selectedCustomer.companyId,
      };

      console.log("📧 Sending email:", emailData);

      const response = await fetch(
        `${baseUrl}/api/send-email/${selectedCustomer.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(emailData),
        }
      );

      const result = await response.json();

      console.log("📨 Backend response:", result);

      if (result.success) {
        alert("✅ Email sent successfully");
        setEmailDialogOpen(false);
        fetchData(currentPage); // refresh email count
      } else {
        alert(result.error || result.message);
      }
    } catch (err) {
      console.error("❌ Send email failed:", err);
      alert("Failed to send email.");
    }
  };
  const handleEdit = async (id, updatedData) => {
    try {
      const endpoint =
        dataSource === "customers" ? "/api/customers" : "/api/marketing";

      if (isOffline) {
        storeOfflineData([updatedData], dataSource, "update");
      } else {
        await saveData(`${endpoint}/${id}`, updatedData);
      }
      fetchData();
    } catch (err) {
      console.error("Error editing record:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      const endpoint =
        dataSource === "customers" ? "/api/customers" : "/api/marketing";

      if (isOffline) {
        storeOfflineData([{ id }], dataSource, "delete");
      } else {
        await deleteData(`${endpoint}/${id}`);
      }
      fetchData();
    } catch (err) {
      console.error("Error deleting record:", err);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchData(newPage, 10);
  };

  const handleSort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchData(currentPage, 10, newFilters);
  };

  // Columns: choose based on data source
  const customerColumns = [
    { header: "Customer", accessor: "name" },
    { header: "Age", accessor: "age", type: "range" },
    { header: "Gender", accessor: "gender" },
    { header: "Type", accessor: "type" },
    { header: "Location", accessor: "location" },
    { header: "Address", accessor: "billing_address" },
    { header: "Date of birth", accessor: "date_of_birth", type: "Date" },
    { header: "Orders", accessor: "number_of_orders" },
    { header: "Phone", accessor: "phone_number" },
    { header: "Contact method", accessor: "preferred_contact_method" },
    {
      header: "Status",
      accessor: "status",
      type: "dropdown",
      options: [
        { value: "active", label: "active" },
        { value: "inactive", label: "inactive" },
      ],
    },
    { header: "Loyalty points", accessor: "loyalty_points", type: "range" },
    { header: "Amount paid", accessor: "amount_paid", type: "range" },
  ];

  const potentialColumns = [
    { header: "Name", accessor: "name" },
    { header: "Email", accessor: "email" },
    { header: "Phone", accessor: "phone" },
    { header: "Interested Product", accessor: "interested_product" },
    { header: "Source", accessor: "source" },
    { header: "Score", accessor: "score", type: "range" },

    // Stage dropdown
    {
      header: "Stage",
      accessor: "stage",
      type: "dropdown",
      options: [
        { value: "new", label: "New" },
        { value: "contacted", label: "Contacted" },
        { value: "qualified", label: "Qualified" },
        { value: "proposal_sent", label: "Proposal Sent" },
        { value: "negotiation", label: "Negotiation" },
        { value: "closed", label: "Closed" },
      ],
      onChange: async (row, value) => {
        await handleEdit(row.id, { stage: value });
      },
    },

    // Priority dropdown
    {
      header: "Priority",
      accessor: "priority",
      type: "dropdown",
      options: [
        { value: "low", label: "Low" },
        { value: "medium", label: "Medium" },
        { value: "high", label: "High" },
      ],
      onChange: async (row, value) => {
        await handleEdit(row.id, { priority: value });
      },
    },

    // Lead Type
    {
      header: "Lead Type",
      accessor: "lead_type",
      type: "dropdown",
      options: [
        { value: "lead", label: "Lead" },
        { value: "rejected", label: "Rejected" },
        { value: "sale", label: "Sale" },
      ],
      onChange: async (row, value) => {
        await handleEdit(row.id, { lead_type: value });
      },
    },

    // Contacted
    {
      header: "Contacted",
      accessor: "contacted",
      type: "dropdown",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
      onChange: async (row, value) => {
        await handleEdit(row.id, { contacted: value });
      },
    },

    // Rejection reason (show only if rejected)
    {
      header: "Rejection Reason",
      accessor: "rejection_reason",
      type: "dropdown",
      options: [
        { value: "price", label: "Price" },
        { value: "no_answer", label: "No Answer" },
        { value: "no_like", label: "Didn’t Like" },
        { value: "not_interested", label: "Not Interested" },
      ],
      disabled: (row) => row.lead_type !== "rejected", // 👈 disable when not rejected
      onChange: async (row, value) => {
        await handleEdit(row.id, { rejection_reason: value });
      },
    },

    // Notes
    { header: "Notes", accessor: "notes" },

    // Communication icons
    // Communication icons
    {
      header: "Email",
      accessor: "email_count",
      type: "icon",
      icon: "email",
      onClick: (row) => {
        setSelectedCustomer(row);
        setEmailForm({
          to: row.email,
          subject: `Hello ${row.name}`,
          body: [
            `Hi ${row.name},`,
            "",
            "Thank you for your interest in our products.",
            "",
            "Please let us know if you have any questions.",
            "",
            "Best regards,",
            "Spade Team",
          ].join("\n"),
        });
        setEmailDialogOpen(true);
      },
    },
    {
      header: "WhatsApp",
      accessor: "whatsapp_count",
      type: "icon",
      icon: "whatsapp",
      onClick: (row) => {
        try {
          const baseMessage = "Hello 👋 from Spade!";
          const customMessage =
            row.customNote || "We’d love to connect with you.";
          const finalMessage = encodeURIComponent(
            `${baseMessage}\n${customMessage}`
          );

          // ✅ Clean country code and phone number
          const countryCode = (
            (dataSource === "customers" ? row.countryCode : row.countryCode) ||
            ""
          ).replace(/\D/g, ""); // remove +, spaces, or letters

          const phone = (
            dataSource === "customers" ? row.phone_number : row.phone
          )
            ?.replace(/\D/g, "") // remove spaces/dashes
            .replace(/^0+/, ""); // remove leading zeros

          // ✅ Combine into full E.164 format (no +, no spaces)
          const fullNumber = `${countryCode}${phone}`;

          if (!fullNumber || fullNumber.length < 8) {
            alert("⚠️ Invalid phone number format.");
            console.log("Invalid fullNumber:", fullNumber);
            return;
          }

          const whatsappUrl = `https://wa.me/${fullNumber}?text=${finalMessage}`;
          console.log("Opening:", whatsappUrl);

          window.open(whatsappUrl, "_blank"); // ✅ open in new tab
        } catch (err) {
          console.error("❌ Error opening WhatsApp:", err);
          alert("Something went wrong while opening WhatsApp.");
        }
      },
    },
    {
      header: "Notification",
      accessor: "notification_count",
      type: "icon",
      icon: "notification",
      onClick: async (row) => {
        await fetch(`${baseUrl}/api/send-notification/${row.id}`, {
          method: "POST",
        });
        fetchData(currentPage);
      },
    },
  ];

  const columns =
    dataSource === "customers" ? customerColumns : potentialColumns;

  return (
    <div>
      <Box sx={{ padding: "20px" }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 700,
            color: "#2E4053",
            marginBottom: "12px",
          }}
        >
          Marketing
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "12px",
            backgroundColor: "#D1D8E0",
            borderRadius: "10px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            width: "220px",
            marginBottom: "20px", // spacing below the box
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "#2E4053",
              marginBottom: "12px",
              textAlign: "center",
            }}
          >
            Select Data Source
          </Typography>

          <FormControl
            fullWidth
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "6px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            }}
            size="small"
          >
            <InputLabel id="data-source-label" sx={{ fontSize: "14px" }}>
              Data Source
            </InputLabel>
            <Select
              labelId="data-source-label"
              value={dataSource}
              label="Data Source"
              onChange={(e) => setDataSource(e.target.value)}
              sx={{
                "& .MuiSelect-select": {
                  padding: "8px",
                  fontSize: "14px",
                },
              }}
            >
              <MenuItem value="customers">Customers</MenuItem>
              <MenuItem value="marketing">Potential Customers</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Dialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Compose Email</DialogTitle>

          <DialogContent>
            <TextField
              margin="normal"
              fullWidth
              label="To"
              value={emailForm.to}
              onChange={(e) => updateEmailField("to", e.target.value)}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Subject"
              value={emailForm.subject}
              onChange={(e) => updateEmailField("subject", e.target.value)}
            />
            <TextField
              margin="normal"
              fullWidth
              multiline
              rows={10}
              label="Message"
              value={emailForm.body}
              onChange={(e) => updateEmailField("body", e.target.value)}
            />
          </DialogContent>

          <DialogActions>
            <Button
              sx={cancelButtonStyles}
              onClick={() => setEmailDialogOpen(false)}
            >
              Cancel
            </Button>

            <Button sx={secondaryButtonStyles} onClick={openInEmailApp}>
              Open Email App
            </Button>

            <Button sx={primaryButtonStyles} onClick={sendEmail}>
              Send From ERP
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <DataTable
        title={dataSource === "customers" ? "Customers" : "Potential Customers"}
        columns={columns}
        data={data}
        currentPage={currentPage}
        setData={setData}
        totalPages={totalPages}
        onSort={handleSort}
        sortOrder={sortOrder}
        sortField={sortField}
        onPageChange={handlePageChange}
        filters={filters}
        onFilterChange={handleFilterChange}
        onAddRow={handleAddRow}
        actions={[
          {
            label: "Edit",
            onClick: (row) => {
              const updatedData = { ...row, name: "Updated Name" };
              handleEdit(row.id, updatedData);
            },
          },
          {
            label: "Delete",
            onClick: (row) => handleDelete(row.id),
          },
        ]}
        apiEndpoint={
          dataSource === "customers" ? "/api/customers" : "/api/marketing"
        }
        itemsPerPage={10}
        fetchData={fetchData}
        totalItems={totalItems}
        getRangeInputProps={getRangeInputProps}
      />
    </div>
  );
};

export default Marketing;
